import React, { useMemo, useState } from "react";
import Header from "./components/Header";
import { ToastContainer } from "./components/Toast";
import { useToast } from "./hooks/useToast";
import { LangProvider } from "./context/LangContext";
import { logMemoryUsage } from "./utils/performance";

// Direct imports to fix lazy loading issues
import StartScreen from "./components/ImprovedHomePageClean";
import FarmDataForm from "./components/FarmDataForm";
import ProgramResults from "./components/ProgramResults";
import CookieBanner from "./components/CookieBanner";
import AboutUsPage from "./components/AboutUsPage";
import PaymentCheckout from "./components/PaymentCheckout";
import PaymentSuccess from "./components/PaymentSuccess";
import InvestorLanding from "./components/InvestorLanding";

// Loading component - removed unused variable

function App() {
  const isInvestor = useMemo(() => {
    if (typeof window === 'undefined') return false;
    if (String(process.env.REACT_APP_FORCE_IR).toLowerCase() === 'true') return true;
    const host = (window.location.hostname || '').toLowerCase();
    const path = (window.location.pathname || '').toLowerCase();
    const qp = new URLSearchParams(window.location.search);
    const forced = qp.get('ir');
    if (forced === '1' || forced === 'true') return true;
    // Subdomain detection (supports ir.centeo.one and www.ir.centeo.one)
    const parts = host.split('.');
    const first = parts[0] || '';
    const second = parts[1] || '';
    const isIrSubdomain = first === 'ir' || (first === 'www' && second === 'ir');
    // Path-based fallback (e.g., /ir)
    const isIrPath = path === '/ir' || path.startsWith('/ir/');
    return isIrSubdomain || isIrPath;
  }, []);

  const [currentView, setCurrentView] = useState("start");
  const [farmData, setFarmData] = useState(null);
  const [results, setResults] = useState(null);
  const { toasts, removeToast, showSuccess } = useToast();

  // Check for payment success on app load
  React.useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    
    if (sessionId) {
      setCurrentView("success");
    }
  }, []);

  // Log memory usage in development
  if (process.env.NODE_ENV === 'development') {
    setTimeout(logMemoryUsage, 1000);
  }

  const handleFormSubmit = (data) => {
    console.log('App received form data:', data);
    setFarmData(data);
    // Save to localStorage as backup
    localStorage.setItem('centeo_farmData', JSON.stringify(data));
    setCurrentView("payment");
    showSuccess("Daten erfasst! Jetzt zur Bezahlung.", 3000);
  };

  const handlePaymentSuccess = (paymentData = null) => {
    // If we have payment data from Stripe, extract farmData
    if (paymentData && paymentData.farmData) {
      setFarmData(paymentData.farmData);
    } else {
      // Fallback: try to get farmData from localStorage
      const savedFarmData = localStorage.getItem('centeo_farmData');
      if (savedFarmData) {
        try {
          const parsedData = JSON.parse(savedFarmData);
          setFarmData(parsedData);
          console.log('🔄 Restored farmData from localStorage:', parsedData);
        } catch (e) {
          console.error('Error parsing saved farmData:', e);
        }
      }
    }
    setCurrentView("results");
    if (paymentData && paymentData.freeMode) {
      showSuccess("Kostenlos freigeschaltet! Ihre Analyse wird erstellt.", 4000);
    } else {
      showSuccess("Zahlung erfolgreich! Ihre Analyse wird erstellt.", 4000);
    }
  };

  const handleBackToForm = () => {
    setCurrentView("form");
    setResults(null);
  };

  const handleBackToStart = () => {
    setCurrentView("start");
    setFarmData(null);
    setResults(null);
    // Clean up localStorage
    localStorage.removeItem('centeo_farmData');
  };

  const handleShowAboutUs = () => {
    setCurrentView("about");
  };

  const handleBackFromAbout = () => {
    setCurrentView("start");
  };

  return (
    <LangProvider>
      <div className="App min-h-screen">
        <Header onLogoClick={handleBackToStart} />
        <main className="pt-20">
          {isInvestor && (
            <InvestorLanding />
          )}
          {!isInvestor && (
            <>
          {currentView === "start" && (
            <StartScreen 
              onStart={() => setCurrentView("form")} 
              onShowAboutUs={handleShowAboutUs}
            />
          )}
          {currentView === "about" && (
            <AboutUsPage onBack={handleBackFromAbout} />
          )}
          {currentView === "form" && (
            <FarmDataForm profile="project_funding" onSubmit={handleFormSubmit} />
          )}
          {currentView === "payment" && (
            <PaymentCheckout 
              farmData={farmData} 
              onPaymentSuccess={handlePaymentSuccess}
              onBack={() => setCurrentView("form")}
            />
          )}
          {currentView === "success" && (
            <PaymentSuccess 
              onContinue={handlePaymentSuccess}
            />
          )}
          {currentView === "results" && (
            <div>
              {/* Navigation breadcrumbs */}
              <div className="container py-4">
                <nav className="flex items-center gap-4 text-lg">
                  <button 
                    onClick={handleBackToStart}
                    className="text-green hover:underline"
                  >
                    Start
                  </button>
                  <span className="text-gray-400">→</span>
                  <button 
                    onClick={handleBackToForm}
                    className="text-green hover:underline"
                  >
                    Formular
                  </button>
                  <span className="text-gray-400">→</span>
                  <span className="text-gray-600">Ergebnisse</span>
                </nav>
              </div>
              
              <ProgramResults 
                farmData={farmData} 
                results={results} 
                setResults={setResults} 
              />
            </div>
          )}
            </>
          )}
          {/* AmpelTestPage disabled in public mode */}
      </main>
      
      {/* Toast Notifications */}
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      
        {/* Cookie Banner */}
        <CookieBanner />
      </div>
    </LangProvider>
  );
}

export default App;
